import React, { Fragment, useEffect, useMemo, useState } from 'react';
import Tablereact from '../common/tabulator/tablereact';
import Button from 'react-bootstrap-button-loader';
import Loader from '../common/loaderapp';
import notie from 'notie';
import '../../assets/css/notie.css';

const ENDPOINT = '/p_unidades';
const DEFAULT_ID_KEY = 'ID_U';
const DEFAULT_NAME_KEY = 'NOMBRE_U';
const DEFAULT_ABR_KEY = 'ABREVIACION_U';
const DEFAULT_TIPO_KEY = 'TIPO_U';

const Unidades = () => {
    const [datos, setDatos] = useState([]);
    const [load, setLoad] = useState(false);
    const [editando, setEditando] = useState(false);
    const [esNuevo, setEsNuevo] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);

    const cargarUnidades = async () => {
        try {
            setLoad(false);
            const response = await fetch(ENDPOINT, { headers: { 'Content-Type': 'application/json' } });
            const payload = await response.json();
            setDatos(Array.isArray(payload) ? payload : []);
            setLoad(true);
        } catch (error) {
            console.log('Error:', error);
            setDatos([]);
            setLoad(true);
            notie.alert({ type: 'error', text: 'No se pudieron cargar las unidades.', time: 2 });
        }
    };

    useEffect(() => {
        cargarUnidades();
    }, []);

    const resolveIdKey = (obj) => {
        if (!obj) return DEFAULT_ID_KEY;
        const key = Object.keys(obj).find(k => k.toLowerCase() === 'id')
            || Object.keys(obj).find(k => k.toLowerCase().includes('id'));
        return key || DEFAULT_ID_KEY;
    };

    const resolveNombreKey = (obj) => {
        if (!obj) return DEFAULT_NAME_KEY;
        const key = Object.keys(obj).find(k => k.toLowerCase().includes('nombre'));
        return key || DEFAULT_NAME_KEY;
    };

    const resolveAbreviacionKey = (obj) => {
        if (!obj) return DEFAULT_ABR_KEY;
        const key = Object.keys(obj).find(k => k.toLowerCase().includes('abrevi'));
        return key || DEFAULT_ABR_KEY;
    };

    const resolveTipoKey = (obj) => {
        if (!obj) return DEFAULT_TIPO_KEY;
        const key = Object.keys(obj).find(k => k.toLowerCase().includes('tipo'));
        return key || DEFAULT_TIPO_KEY;
    };

    const idKey = useMemo(() => resolveIdKey(datos[0] || formData), [datos, formData]);
    const nombreKey = useMemo(() => resolveNombreKey(datos[0] || formData), [datos, formData]);
    const abreviacionKey = useMemo(() => resolveAbreviacionKey(datos[0] || formData), [datos, formData]);
    const tipoKey = useMemo(() => resolveTipoKey(datos[0] || formData), [datos, formData]);

    const cabecera = useMemo(() => {
        if (!datos.length) return [];
        const keys = Array.from(
            datos.reduce((set, row) => {
                Object.keys(row || {}).forEach((key) => set.add(key));
                return set;
            }, new Set())
        );
        const nombreColumn = keys.find((key) => key === nombreKey) || nombreKey;
        const abreviacionColumn = keys.find((key) => key === abreviacionKey) || abreviacionKey;
        const columnas = [
            { key: nombreColumn, label: 'Nombre' },
            { key: abreviacionColumn, label: 'Abreviacion' }
        ].filter((col) => col.key);
        return columnas.map((col) => ({
            Header: <b>{col.label}</b>,
            accessor: col.key,
            show: true,
            style: { textAlign: 'center' }
        }));
    }, [datos, nombreKey, abreviacionKey]);

    const iniciarAlta = () => {
        setFormData({ [nombreKey]: '', [abreviacionKey]: '', [tipoKey]: 1 });
        setEsNuevo(true);
        setEditando(true);
    };

    const iniciarEdicion = (dato) => {
        const tipoValue = normalizeTipo(dato?.[tipoKey]);
        setFormData({ ...dato, [tipoKey]: tipoValue });
        setEsNuevo(false);
        setEditando(true);
    };

    const cancelarEdicion = () => {
        setEditando(false);
        setEsNuevo(false);
        setFormData({});
    };

    const normalizeTipo = (value) => {
        const parsed = Number(value);
        return parsed === 2 ? 2 : 1;
    };

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const guardarUnidad = async () => {
        try {
            const nombre = String(formData[nombreKey] ?? '').trim();
            if (!nombre) {
                notie.alert({ type: 'warning', text: 'Asigna un nombre de unidad.', time: 2 });
                return;
            }
            const tipoValue = Number(formData[tipoKey]);
            if (![1, 2].includes(tipoValue)) {
                notie.alert({ type: 'warning', text: 'Selecciona el tipo de unidad.', time: 2 });
                return;
            }
            setSaving(true);
            const response = await fetch(ENDPOINT, {
                method: esNuevo ? 'POST' : 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, [tipoKey]: tipoValue })
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload?.status === 'ERROR') {
                throw new Error(payload?.resultado || 'No se pudo guardar la unidad');
            }
            await cargarUnidades();
            cancelarEdicion();
            notie.alert({
                type: 'success',
                text: esNuevo ? 'Unidad registrada correctamente.' : 'Unidad actualizada correctamente.',
                time: 2
            });
        } catch (error) {
            console.log(error);
            notie.alert({ type: 'error', text: error?.message || 'No se pudo guardar la unidad', time: 3 });
        } finally {
            setSaving(false);
        }
    };

    const eliminarUnidad = async (dato) => {
        if (!dato) return;
        try {
            setSaving(true);
            const idValue = dato[idKey] ?? dato.ID ?? dato.id;
            const response = await fetch(ENDPOINT, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ [idKey]: idValue, ID: idValue, id: idValue })
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload?.status === 'ERROR') {
                throw new Error(payload?.resultado || 'No se pudo eliminar la unidad');
            }
            await cargarUnidades();
            notie.alert({ type: 'success', text: 'Unidad eliminada correctamente.', time: 2 });
        } catch (error) {
            console.log(error);
            notie.alert({ type: 'error', text: error?.message || 'No se pudo eliminar la unidad', time: 3 });
        } finally {
            setSaving(false);
        }
    };

    const renderFormulario = () => {
        const label = nombreKey === DEFAULT_NAME_KEY ? 'Nombre' : nombreKey;
        const abreviacionLabel = abreviacionKey === DEFAULT_ABR_KEY ? 'Abreviación' : abreviacionKey;
        const tipoLabel = tipoKey === DEFAULT_TIPO_KEY ? 'Tipo' : tipoKey;
        const tipoValue = normalizeTipo(formData[tipoKey]);
        return (
            <Fragment>
                <div className="card-header" style={{ padding: '15px' }}>
                    <button className="btn btn-outline-light-2x txt-dark" onClick={cancelarEdicion} disabled={saving}>
                        <i className="icofont icofont-arrow-left" />Regresar
                    </button>
                </div>
                <h4 className="mt-3 ml-4">
                    <strong>{esNuevo ? 'Agregar Unidad' : 'Editar Unidad'}</strong>
                </h4>
                <div className="card-body">
                    <div className="row">
                        <div className="col-12">
                            <div className="form-group">
                                <label className="form-label">{label}</label>
                                <input
                                    name={nombreKey}
                                    value={formData[nombreKey] ?? ''}
                                    onChange={(e) => handleChange(nombreKey, e.target.value)}
                                    className="form-control"
                                    type="text"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="form-group">
                                <label className="form-label">{abreviacionLabel}</label>
                                <input
                                    name={abreviacionKey}
                                    value={formData[abreviacionKey] ?? ''}
                                    onChange={(e) => handleChange(abreviacionKey, e.target.value)}
                                    className="form-control"
                                    type="text"
                                    autoComplete="off"
                                    maxLength={10}
                                />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="form-group">
                                <label className="form-label">{tipoLabel}</label>
                                <select
                                    name={tipoKey}
                                    value={tipoValue}
                                    onChange={(e) => handleChange(tipoKey, normalizeTipo(e.target.value))}
                                    className="form-control"
                                >
                                    <option value={1}>Menudeo</option>
                                    <option value={2}>Mayoreo</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12 mt-2">
                        <div className="text-right">
                            <Button className="btn-lg mr-4" onClick={guardarUnidad} loading={saving}>Guardar</Button>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    };

    return (
        <div className="col-sm-12">
            <div className="card">
                {load ? (
                    editando ? (
                        renderFormulario()
                    ) : (
                        <Fragment>
                            <div className="card-body datatable-react">
                                <Tablereact
                                    myData={datos}
                                    cabecera={cabecera}
                                    pageSize={10}
                                    paginacion={true}
                                    buscador="Buscar"
                                    boton={{ text: 'Agregar Unidad', function: iniciarAlta }}
                                    eliminar_actualizar={{ actualizar: iniciarEdicion, eliminar: eliminarUnidad }}
                                />
                            </div>
                        </Fragment>
                    )
                ) : (
                    <Loader />
                )}
            </div>
        </div>
    );
};

export default Unidades;
